import React, { useEffect, useRef } from 'react';
import { Code2, Shield, TrendingUp, Zap } from 'lucide-react';
import { siteContent } from '../content/siteContent';

const featureIconMap = {
  code2: Code2,
  zap: Zap,
  trendingUp: TrendingUp,
  shield: Shield,
};

const About = () => {
  const sectionRef = useRef(null);
  const { about } = siteContent;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.fade-in-up');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#D22527] rounded-full filter blur-3xl opacity-10"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-20">
          <h2 className="fade-in-up text-5xl md:text-6xl font-bold text-white mb-6">
            {about.heading[0]}{' '}
            <span className="bg-gradient-to-r from-[#D22527] to-[#88171A] bg-clip-text text-transparent">
              {about.heading[1]}
            </span>
            <br />
            {about.heading[2]}
          </h2>
          <p className="fade-in-up text-xl text-gray-400 max-w-3xl mx-auto">{about.intro}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {about.features.map((feature, index) => {
            const Icon = featureIconMap[feature.icon];
            return (
              <div
                key={index}
                className="fade-in-up group p-8 rounded-3xl bg-gradient-to-br from-[#D22527]/5 to-transparent border border-[#D22527]/20 hover:border-[#D22527]/50 transition-all duration-500 hover:scale-105 backdrop-blur-sm"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-[#D22527] to-[#88171A] flex items-center justify-center group-hover:scale-110 transition-transform duration-500 group-hover:rotate-6">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#D22527] transition-colors duration-300">
                      {feature.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="fade-in-up text-center max-w-4xl mx-auto p-12 rounded-3xl bg-gradient-to-br from-[#D22527]/10 to-transparent border border-[#D22527]/30 backdrop-blur-sm">
          <p className="text-2xl md:text-3xl text-white leading-relaxed font-light">
            We help companies move from{' '}
            <span className="font-bold text-[#D22527]">{about.missionLead[0].text}</span>,{' '}
            from <span className="font-bold text-[#D22527]">{about.missionLead[1].text}</span>,{' '}
            and from <span className="font-bold text-[#D22527]">{about.missionLead[2].text}</span>.
          </p>
          <p className="mt-8 text-xl text-gray-400">{about.missionBody}</p>
        </div>
      </div>
    </section>
  );
};

export default About;
